import React from 'react';
import { roundToDecimals } from 'utils/shared/number/roundToDecimals';
import { StrategyBuilder } from 'utils/shared/quantlib/StrategyBuilder';

// A greek of exactly 0 is a real, meaningful value (e.g. a perfectly hedged
// delta, or gamma/theta/vega at expiration). The old `!!value && …` guards
// treated 0 as falsy and rendered nothing, so legitimate zeros "disappeared".
// Guard on finiteness instead: show the number when finite, an em dash when not.
const fmt = (value: number | undefined | null, decimals = 2): string =>
  Number.isFinite(value) ? String(roundToDecimals(value as number, decimals)) : '—';

interface Props {
  strategyMetrics?: ReturnType<StrategyBuilder['calculateStrategyMetrics']>;
  volatility: number | null;
  isManual?: boolean;
}

export function GreekLetters({ strategyMetrics, volatility, isManual = false }: Props) {
  return (
    <div className="mx-auto w-full max-w-4xl p-2">
      <ul
        role="list"
        className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3"
      >
        <li className="col-span-1 mb-0 flex flex-col items-center justify-center rounded-md text-center">
          <p className="mb-0.5 text-xs font-semibold">Delta</p>
          <p className="flex items-center justify-center gap-x-2">
            <span className="text-top-position">Δ</span>
            <span className="text-xs font-medium tracking-tight text-white">
              {fmt(strategyMetrics?.delta)}
            </span>
          </p>
        </li>
        <li className="col-span-1 mb-0 flex flex-col items-center justify-center rounded-md text-center">
          <p className="mb-0.5 text-xs font-semibold">Gamma</p>
          <p className="flex items-center justify-center gap-x-2">
            <span className="text-graph-red">Γ</span>
            <span className="text-xs font-medium tracking-tight text-white">
              {fmt(strategyMetrics?.gamma)}
            </span>
          </p>
        </li>
        <li className="col-span-1 mb-0 flex flex-col items-center justify-center rounded-md text-center">
          <p className="mb-0.5 text-xs font-semibold">Theta</p>
          <p className="flex items-center justify-center gap-x-2">
            <span className="text-graph-purple">Θ</span>
            <span className="text-xs font-medium tracking-tight text-white">
              {fmt(strategyMetrics?.theta)}
            </span>
          </p>
        </li>
        <li className="col-span-1 mb-0 flex flex-col items-center justify-center rounded-md text-center">
          <p className="mb-0.5 text-xs font-semibold">Vega</p>
          <p className="flex items-center justify-center gap-x-2">
            <span className="text-graph-yellow">ν</span>
            <span className="text-xs font-medium tracking-tight text-white">
              {fmt(strategyMetrics?.vega)}
            </span>
          </p>
        </li>
        <li className="col-span-1 mb-0 flex flex-col items-center justify-center rounded-md text-center">
          <p className="mb-0.5 text-xs font-semibold">Rho</p>
          <p className="flex items-center justify-center gap-x-2">
            <span className="text-brand-green-primary">ρ</span>
            <span className="text-xs font-medium tracking-tight text-white">
              {fmt(strategyMetrics?.rho)}
            </span>
          </p>
        </li>
        <li className="col-span-1 mb-0 flex flex-col items-center justify-center rounded-md text-center">
          <p className="mb-0.5 text-xs font-semibold">
            {isManual ? 'Estimated volatility' : 'Volatility'}
          </p>
          <p className="flex items-center justify-center gap-x-2">
            <span className="text-electric-teal">σ</span>
            <span className="text-xs font-medium tracking-tight text-white">
              {volatility != null && Number.isFinite(volatility)
                ? `${roundToDecimals(volatility * 100, 2)}%`
                : '—'}
            </span>
          </p>
        </li>
      </ul>
    </div>
  );
}
